import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import LoginForm from '../components/auth/LoginForm';
import RegisterForm from '../components/auth/RegisterForm';
import MandatoryPasswordChangeScenario from '../components/auth/MandatoryPasswordChangeScenario';
import PasswordInput from '../components/auth/PasswordInput';
import { useAuth } from '../context/AuthContext';
import { authAPI } from '../services/api';

function ForgotPasswordPanel({ onBack }) {
  const [email, setEmail] = useState('');
  const [msg, setMsg] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setMsg('');
    setLoading(true);
    try {
      await authAPI.forgotPassword(email.trim().toLowerCase());
      setMsg(
        'If that email is registered, a reset link was generated. In development, check the API server logs for the link.',
      );
    } catch (err) {
      setError(err?.response?.data?.detail || 'Request failed. Try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="auth-brand">
        <img src="/teachiq-logo.png" alt="TeachIQ" className="auth-brand-logo" />
        <div className="auth-brand-text">
          <span className="auth-brand-tagline">Smarter Teaching, Longer Impact</span>
          <span className="auth-brand-sub">A Flowgenix Labs Product</span>
        </div>
      </div>
    <div className="auth-card">
      <h1 className="auth-title">Reset password</h1>
      <p className="auth-subtitle">Enter your email. We will send reset instructions if the account exists.</p>
      {msg && <div className="auth-error" style={{ background: 'rgba(34,197,94,0.12)', color: '#4ade80' }}>{msg}</div>}
      {error && <div className="auth-error">{error}</div>}
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label>Email</label>
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
        </div>
        <button type="submit" className="btn-primary" disabled={loading}>
          {loading ? '…' : 'Send reset link'}
        </button>
      </form>
      <p className="auth-switch">
        <button type="button" className="link-btn" onClick={onBack}>Back to sign in</button>
      </p>
    </div>
    </>
  );
}

function ResetPasswordPanel({ token, onDone }) {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }
    setLoading(true);
    try {
      await authAPI.resetPassword(token, password);
      onDone();
    } catch (err) {
      setError(err?.response?.data?.detail || 'Reset failed. The link may have expired.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="auth-brand">
        <img src="/teachiq-logo.png" alt="TeachIQ" className="auth-brand-logo" />
        <div className="auth-brand-text">
          <span className="auth-brand-tagline">Smarter Teaching, Longer Impact</span>
          <span className="auth-brand-sub">A Flowgenix Labs Product</span>
        </div>
      </div>
    <div className="auth-card">
      <h1 className="auth-title">Choose a new password</h1>
      {error && <div className="auth-error">{error}</div>}
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label>New password</label>
          <PasswordInput
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            minLength={8}
            autoComplete="new-password"
          />
        </div>
        <div className="form-group">
          <label>Confirm password</label>
          <PasswordInput
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            required
            minLength={8}
            autoComplete="new-password"
          />
        </div>
        <button type="submit" className="btn-primary" disabled={loading}>
          {loading ? '…' : 'Update password'}
        </button>
      </form>
    </div>
    </>
  );
}

export default function AuthPage() {
  const { learner, isAuthenticated } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const resetToken = searchParams.get('reset_token');
  const [mode, setMode] = useState(() => (resetToken ? 'reset' : 'login'));

  useEffect(() => {
    if (resetToken) {
      setMode('reset');
    }
  }, [resetToken]);

  if (isAuthenticated && learner?.password_change_required) {
    return (
      <div className="auth-page">
        <MandatoryPasswordChangeScenario />
      </div>
    );
  }

  if (mode === 'reset' && resetToken) {
    return (
      <div className="auth-page">
        <ResetPasswordPanel
          token={resetToken}
          onDone={() => {
            searchParams.delete('reset_token');
            setSearchParams(searchParams, { replace: true });
            setMode('login');
          }}
        />
      </div>
    );
  }

  return (
    <div className="auth-page">
      {mode === 'login' && (
        <LoginForm
          onSwitch={() => setMode('register')}
          onForgot={() => setMode('forgot')}
        />
      )}
      {mode === 'register' && (
        <RegisterForm onSwitch={() => setMode('login')} />
      )}
      {mode === 'forgot' && (
        <ForgotPasswordPanel onBack={() => setMode('login')} />
      )}
    </div>
  );
}
